import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, User, LogOut, LayoutDashboard, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { api } from "@/lib/api";
import Ticker from "@/components/Ticker";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_62fe82b4-8a19-46f2-ac06-6bb7b1c3a21c/artifacts/jzp1y8sq_Aham-Yoga-Institute-logo.png";

const NAV_LINKS = [
    { label: "Home", hash: "#home" },
    { label: "Membership", to: "/membership" },
    { label: "Gallery", to: "/media" },
    { label: "About Us", to: "/about" },
    { label: "Contact", hash: "#contact" },
];

function CategoriesMegaDropdown({ label, testId, categories, featured, featuredLabel, viewAllHash, getCategoryTo, getFeaturedTo, active }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className={
                        active
                            ? "px-4 py-2 text-sm font-semibold text-brand-600 transition-colors inline-flex items-center gap-1 relative after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-brand-500"
                            : "px-4 py-2 text-sm font-medium text-ink-700 hover:text-brand-600 transition-colors inline-flex items-center gap-1"
                    }
                    data-testid={`nav-${testId}-trigger`}
                    aria-current={active ? "page" : undefined}
                >
                    {label}
                    <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 p-2" data-testid={`nav-${testId}-menu`}>
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold px-2">
                    Browse by category
                </DropdownMenuLabel>
                {categories.length === 0 ? (
                    <DropdownMenuItem disabled className="text-xs text-ink-700">No categories yet</DropdownMenuItem>
                ) : (
                    categories.map((c) => (
                        <DropdownMenuItem key={c.id} asChild>
                            <Link
                                to={getCategoryTo(c)}
                                className="w-full cursor-pointer rounded-lg"
                                data-testid={`nav-${testId}-cat-${c.id}`}
                            >
                                {c.name}
                            </Link>
                        </DropdownMenuItem>
                    ))
                )}
                {featured && featured.length > 0 && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-ink-400 font-semibold px-2">
                            {featuredLabel}
                        </DropdownMenuLabel>
                        {featured.map((f) => (
                            <DropdownMenuItem key={f.id} asChild>
                                <Link
                                    to={getFeaturedTo(f)}
                                    className="w-full cursor-pointer rounded-lg flex items-center gap-2"
                                    data-testid={`nav-${testId}-featured-${f.id}`}
                                >
                                    {f.image_url && (
                                        <span className="h-8 w-8 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                                            <img src={f.image_url} alt="" className="w-full h-full object-cover" />
                                        </span>
                                    )}
                                    <span className="truncate text-sm">{f.title || f.name}</span>
                                </Link>
                            </DropdownMenuItem>
                        ))}
                    </>
                )}
                {viewAllHash && (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                            <Link to={viewAllHash} className="w-full cursor-pointer rounded-lg text-brand-600 font-medium" data-testid={`nav-${testId}-view-all`}>
                                View all →
                            </Link>
                        </DropdownMenuItem>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function Navbar({ onOpenAuth }) {
    const { user, logout } = useAuth();
    const { count, setOpen } = useCart();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false);
    const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
    const [courseCats, setCourseCats] = useState([]);
    const [productCats, setProductCats] = useState([]);
    const [featuredCourses, setFeaturedCourses] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            api.get("/course-categories").then((r) => r.data).catch(() => []),
            api.get("/categories").then((r) => r.data).catch(() => []),
            api.get("/courses").then((r) => r.data).catch(() => []),
        ]).then(([cc, pc, cs]) => {
            setCourseCats(cc || []);
            setProductCats(pc || []);
            setFeaturedCourses((cs || []).slice(0, 3));
        });
    }, []);

    const isAuthed = user && typeof user === "object";
    const isStaff = isAuthed && ["admin", "shop_manager"].includes(user.role);

    // Active-route detection for header nav. Hash-only links (#about, #contact)
    // are scroll targets on the home page, so we only highlight them when the
    // user has explicitly navigated to that hash on `/`.
    const path = location.pathname;
    const hash = location.hash;
    const isHomeActive = path === "/" && (hash === "" || hash === "#home");
    const isCoursesActive = path.startsWith("/courses");
    const isProductsActive = path.startsWith("/products");
    const isLinkActive = (l) => {
        if (l.to) return path === l.to || path.startsWith(`${l.to}/`);
        if (l.hash) return path === "/" && hash === l.hash;
        return false;
    };
    const activeBtn =
        "px-4 py-2 text-sm font-semibold text-brand-600 transition-colors relative after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-0.5 after:h-0.5 after:rounded-full after:bg-brand-500";
    const inactiveBtn =
        "px-4 py-2 text-sm font-medium text-ink-700 hover:text-brand-600 transition-colors";
    const activeMobile =
        "px-3 py-3 rounded-xl text-base font-semibold text-brand-700 bg-brand-50 transition text-left border-l-4 border-brand-500";
    const inactiveMobile =
        "px-3 py-3 rounded-xl text-base font-medium text-ink-900 hover:bg-brand-50 hover:text-brand-700 transition text-left";

    const goToSection = (hash) => {
        if (location.pathname === "/") {
            const id = hash.replace("#", "");
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(`/${hash}`);
        }
        setMobileOpen(false);
    };

    return (
        <header
            className="sticky top-0 z-50 bg-white/75 backdrop-blur-xl border-b border-gray-100"
            data-testid="navbar"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-20 items-center justify-between gap-4">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
                        <span className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl overflow-hidden ring-1 ring-gray-200 bg-white flex items-center justify-center p-1">
                            <img src={LOGO_URL} alt="Aham Yog" className="max-h-full max-w-full object-contain" />
                        </span>
                        <span className="hidden sm:flex flex-col leading-tight">
                            <span className="font-heading font-semibold text-lg text-ink-900">
                                Aham Yog
                            </span>
                            <span className="text-[11px] uppercase tracking-[0.18em] text-ink-700">
                                Institute
                            </span>
                        </span>
                    </Link>

                    {/* Desktop Nav */}
                    <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
                        <button
                            onClick={() => goToSection("#home")}
                            className={isHomeActive ? activeBtn : inactiveBtn}
                            data-testid="nav-home"
                            aria-current={isHomeActive ? "page" : undefined}
                        >
                            Home
                        </button>

                        <CategoriesMegaDropdown
                            label="Courses"
                            testId="courses"
                            categories={courseCats}
                            featured={featuredCourses}
                            featuredLabel="Featured courses"
                            viewAllHash="/courses"
                            getCategoryTo={(c) => `/courses?category_id=${c.id}`}
                            getFeaturedTo={(c) => `/courses/${c.id}`}
                            active={isCoursesActive}
                        />

                        <CategoriesMegaDropdown
                            label="Products"
                            testId="products"
                            categories={productCats}
                            featured={null}
                            featuredLabel=""
                            viewAllHash="/products"
                            getCategoryTo={(c) => `/products?category_id=${c.id}`}
                            getFeaturedTo={() => "/products"}
                            active={isProductsActive}
                        />

                        {NAV_LINKS.filter((l) => l.label !== "Home").map((l) => {
                            const active = isLinkActive(l);
                            return (
                                <button
                                    key={l.label}
                                    onClick={() => l.to ? navigate(l.to) : goToSection(l.hash)}
                                    className={active ? activeBtn : inactiveBtn}
                                    data-testid={`nav-${l.label.toLowerCase()}`}
                                    aria-current={active ? "page" : undefined}
                                >
                                    {l.label}
                                </button>
                            );
                        })}
                    </nav>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        {isAuthed ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        className="h-10 w-10 rounded-full bg-brand-50 text-brand-700 flex items-center justify-center hover:bg-brand-100 transition"
                                        data-testid="account-menu-trigger"
                                        aria-label="My account"
                                    >
                                        <User className="h-5 w-5" strokeWidth={1.6} />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel className="font-heading">
                                        Hi, {user.name?.split(" ")[0] || "Yogi"}
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem disabled className="text-xs text-ink-700">
                                        {user.email}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="cursor-pointer"
                                        onClick={() => navigate("/account")}
                                        data-testid="account-dashboard-link"
                                    >
                                        <User className="mr-2 h-4 w-4" /> My Account
                                    </DropdownMenuItem>
                                    {isStaff && (
                                        <DropdownMenuItem
                                            className="cursor-pointer"
                                            onClick={() => navigate("/admin")}
                                            data-testid="account-admin-link"
                                        >
                                            <LayoutDashboard className="mr-2 h-4 w-4" /> {user.role === "admin" ? "Admin Panel" : "Manager Dashboard"}
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        className="cursor-pointer"
                                        onClick={logout}
                                        data-testid="account-logout"
                                    >
                                        <LogOut className="mr-2 h-4 w-4" /> Sign out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <button
                                onClick={onOpenAuth}
                                className="h-10 w-10 rounded-full bg-gray-100 hover:bg-brand-50 hover:text-brand-700 text-ink-900 flex items-center justify-center transition"
                                data-testid="nav-signin"
                                aria-label="Sign in"
                            >
                                <User className="h-5 w-5" strokeWidth={1.6} />
                            </button>
                        )}

                        <button
                            onClick={() => setOpen(true)}
                            className="relative h-10 w-10 rounded-full bg-gray-100 hover:bg-brand-50 hover:text-brand-700 text-ink-900 flex items-center justify-center transition"
                            data-testid="nav-cart"
                            aria-label="Open cart"
                        >
                            <ShoppingCart className="h-5 w-5" strokeWidth={1.6} />
                            {count > 0 && (
                                <span
                                    className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-accent-500 text-white text-[11px] font-semibold flex items-center justify-center shadow-accent"
                                    data-testid="navbar-cart-badge"
                                >
                                    {count}
                                </span>
                            )}
                        </button>

                        {!isAuthed && (
                            <Button
                                onClick={onOpenAuth}
                                className="hidden md:inline-flex rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta px-5"
                                data-testid="navbar-signin-btn"
                            >
                                Sign in
                            </Button>
                        )}

                        <button
                            className="lg:hidden h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center"
                            onClick={() => setMobileOpen((v) => !v)}
                            data-testid="navbar-mobile-toggle"
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                {mobileOpen && (
                    <div
                        className="lg:hidden border-t border-gray-100 max-h-[calc(100vh-5rem)] overflow-y-auto py-3 grid gap-1"
                        data-testid="navbar-mobile-menu"
                    >
                        <button
                            onClick={() => { setMobileOpen(false); goToSection("#home"); }}
                            className={isHomeActive ? activeMobile : inactiveMobile}
                            aria-current={isHomeActive ? "page" : undefined}
                        >
                            Home
                        </button>
                        <button
                            type="button"
                            onClick={() => setMobileCoursesOpen((v) => !v)}
                            aria-expanded={mobileCoursesOpen}
                            className={`flex items-center justify-between px-3 py-3 rounded-xl text-base font-semibold text-left transition ${isCoursesActive ? "text-brand-700 bg-brand-50" : "text-ink-900 hover:bg-brand-50 hover:text-brand-700"}`}
                            data-testid="nav-mobile-courses-toggle"
                        >
                            <span>Courses</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${mobileCoursesOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                        </button>
                        {mobileCoursesOpen && courseCats.map((c) => (
                            <Link
                                key={c.id}
                                to={`/courses?category_id=${c.id}`}
                                onClick={() => setMobileOpen(false)}
                                className="px-5 py-2 rounded-lg text-sm font-medium text-ink-900 hover:bg-brand-50 hover:text-brand-700 transition"
                                data-testid={`nav-mobile-course-cat-${c.id}`}
                            >
                                {c.name}
                            </Link>
                        ))}
                        <button
                            type="button"
                            onClick={() => setMobileProductsOpen((v) => !v)}
                            aria-expanded={mobileProductsOpen}
                            className={`flex items-center justify-between px-3 py-3 rounded-xl text-base font-semibold text-left transition ${isProductsActive ? "text-brand-700 bg-brand-50" : "text-ink-900 hover:bg-brand-50 hover:text-brand-700"}`}
                            data-testid="nav-mobile-products-toggle"
                        >
                            <span>Products</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${mobileProductsOpen ? "rotate-180" : ""}`} strokeWidth={2} />
                        </button>
                        {mobileProductsOpen && productCats.map((c) => (
                            <Link
                                key={c.id}
                                to={`/products?category_id=${c.id}`}
                                onClick={() => setMobileOpen(false)}
                                className="px-5 py-2 rounded-lg text-sm font-medium text-ink-900 hover:bg-brand-50 hover:text-brand-700 transition"
                                data-testid={`nav-mobile-product-cat-${c.id}`}
                            >
                                {c.name}
                            </Link>
                        ))}
                        <div className="h-px bg-gray-100 my-2" />
                        {NAV_LINKS.filter((l) => l.label !== "Home").map((l) => {
                            const active = isLinkActive(l);
                            return (
                                <button
                                    key={l.label}
                                    onClick={() => { setMobileOpen(false); l.to ? navigate(l.to) : goToSection(l.hash); }}
                                    className={active ? activeMobile : inactiveMobile}
                                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                                    aria-current={active ? "page" : undefined}
                                >
                                    {l.label}
                                </button>
                            );
                        })}
                        {!isAuthed && (
                            <>
                                <div className="h-px bg-gray-100 my-2" />
                                <Button
                                    onClick={() => { setMobileOpen(false); onOpenAuth?.(); }}
                                    className="mx-3 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                                    data-testid="navbar-mobile-signin-btn"
                                >
                                    Sign in
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </div>
            <Ticker />
            <Breadcrumbs />
        </header>
    );
}
